import { cn } from '#app/utils/misc.tsx'

type Tone = 'hot' | 'warm' | 'cool' | 'muted'

export interface RelativeTime {
	label: string
	tone: Tone
	isPast: boolean
}

/**
 * Computes a French relative-time label for an event date.
 * Bucket boundaries mirror how campaign events are scanned on a phone.
 */
export function getRelativeTimeLabel(
	eventDate: string | Date,
	now: Date = new Date(),
): RelativeTime {
	const startOfToday = new Date(now)
	startOfToday.setHours(0, 0, 0, 0)
	const event = new Date(eventDate)
	const diffDays = Math.floor(
		(event.getTime() - startOfToday.getTime()) / 86_400_000,
	)

	if (diffDays < 0) return { label: 'Passé', tone: 'muted', isPast: true }
	if (diffDays === 0)
		return { label: "Aujourd'hui", tone: 'hot', isPast: false }
	if (diffDays === 1) return { label: 'Demain', tone: 'hot', isPast: false }
	if (diffDays <= 7)
		return { label: 'Cette semaine', tone: 'warm', isPast: false }
	if (diffDays <= 14)
		return { label: 'Semaine prochaine', tone: 'warm', isPast: false }
	const weeks = Math.ceil(diffDays / 7)
	if (weeks <= 8)
		return { label: `Dans ${weeks} semaines`, tone: 'cool', isPast: false }
	const months = Math.ceil(diffDays / 30)
	return { label: `Dans ${months} mois`, tone: 'cool', isPast: false }
}

const MONTH_ABBR_FR = [
	'janv.',
	'févr.',
	'mars',
	'avr.',
	'mai',
	'juin',
	'juill.',
	'août',
	'sept.',
	'oct.',
	'nov.',
	'déc.',
] as const

/** Returns `19 h 30` / `19 h` / null (if midnight, treated as unspecified). */
export function formatEventTime(date: Date): string | null {
	const hours = date.getHours()
	const minutes = date.getMinutes()
	if (hours === 0 && minutes === 0) return null
	if (minutes === 0) return `${hours} h`
	return `${hours} h ${String(minutes).padStart(2, '0')}`
}

/** Long absolute format: `mar. 28 avril 2026`. */
export function formatEventDateLong(date: Date): string {
	return new Intl.DateTimeFormat('fr-CA', {
		weekday: 'short',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	}).format(date)
}

// ---------------------------------------------------------------------------
// Week-calendar helpers (local timezone). One date interpretation everywhere:
// bucketing events into day columns AND "today" detection both go through
// dayKey, so events near midnight never drift onto the wrong day.
// ---------------------------------------------------------------------------

/** Local `YYYY-MM-DD` key — the single source of truth for "same day". */
export function dayKey(date: Date): string {
	const y = date.getFullYear()
	const m = String(date.getMonth() + 1).padStart(2, '0')
	const d = String(date.getDate()).padStart(2, '0')
	return `${y}-${m}-${d}`
}

/** Monday-anchored start of the week at local 00:00 (Sunday → previous Monday). */
export function startOfWeek(date: Date): Date {
	const d = new Date(date)
	d.setHours(0, 0, 0, 0)
	const day = d.getDay() // 0 = Sunday … 6 = Saturday
	d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day))
	return d
}

/** `Mardi` (long) or `MAR` (short, uppercased) in fr-CA. */
export function formatWeekday(date: Date, length: 'long' | 'short'): string {
	const label = new Intl.DateTimeFormat('fr-CA', { weekday: length }).format(
		date,
	)
	if (length === 'short') return label.replace('.', '').toUpperCase()
	return label.charAt(0).toUpperCase() + label.slice(1)
}

/**
 * Week range label: `15 – 21 juin`, or `29 juin – 5 juill.` across months.
 * The start month is shown only when it differs from the end month.
 */
export function formatWeekRange(weekStart: Date): string {
	const weekEnd = new Date(weekStart)
	weekEnd.setDate(weekEnd.getDate() + 6)
	const startMonth = MONTH_ABBR_FR[weekStart.getMonth()]
	const endMonth = MONTH_ABBR_FR[weekEnd.getMonth()]
	const start =
		startMonth === endMonth
			? `${weekStart.getDate()}`
			: `${weekStart.getDate()} ${startMonth}`
	return `${start} – ${weekEnd.getDate()} ${endMonth}`
}

/** Day + month, no weekday: `16 juin`. */
export function formatDayMonth(date: Date): string {
	return `${date.getDate()} ${MONTH_ABBR_FR[date.getMonth()]}`
}

/** Capitalised full date label: `Mardi 16 juin`. */
export function formatFullDateLabel(date: Date): string {
	return `${formatWeekday(date, 'long')} ${formatDayMonth(date)}`
}

// ---------------------------------------------------------------------------
// Date block — pure typography (no container). Pairs with a red spine
// border on the parent row to get the "bookmark / editorial" feel.
// ---------------------------------------------------------------------------

interface EventDateStubProps {
	date: Date
	isPast?: boolean
	size?: 'sm' | 'md'
}

export function EventDateStub({
	date,
	isPast = false,
	size = 'md',
}: EventDateStubProps) {
	const day = date.getDate()
	const monthShort = MONTH_ABBR_FR[date.getMonth()]

	const monthText =
		size === 'sm' ? 'text-[9px]' : 'text-[10px] sm:text-[11px]'
	const dayText = size === 'sm' ? 'text-2xl' : 'text-3xl sm:text-4xl'
	const width = size === 'sm' ? 'w-10' : 'w-12 sm:w-14'

	return (
		<div
			className={cn('flex shrink-0 flex-col items-start', width)}
			aria-hidden="true"
		>
			<span
				className={cn(
					'font-semibold tracking-[0.15em] uppercase',
					monthText,
					isPast ? 'text-muted-foreground' : 'text-plq-rouge',
				)}
			>
				{monthShort}
			</span>
			<span
				className={cn(
					'mt-0.5 font-bold leading-none tabular-nums',
					dayText,
					isPast ? 'text-muted-foreground' : 'text-foreground',
				)}
			>
				{day}
			</span>
		</div>
	)
}

/** Left-border classes to pair with the EventDateStub (spine). */
export function eventRowSpineClass(isPast = false): string {
	return isPast
		? 'border-l-2 border-l-muted-foreground/25'
		: 'border-l-2 border-l-plq-rouge'
}

// ---------------------------------------------------------------------------
// Relative time pill
// ---------------------------------------------------------------------------

const TONE_CLASSES: Record<Tone, string> = {
	hot: 'bg-plq-rouge/10 text-plq-rouge',
	warm: 'bg-accent/15 text-accent',
	cool: 'bg-muted text-muted-foreground',
	muted: 'bg-muted text-muted-foreground',
}

interface EventTimePillProps {
	label: string
	tone: Tone
}

export function EventTimePill({ label, tone }: EventTimePillProps) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
				TONE_CLASSES[tone],
			)}
		>
			{tone === 'hot' && (
				<span
					className="bg-plq-rouge h-1.5 w-1.5 animate-pulse rounded-full"
					aria-hidden="true"
				/>
			)}
			{label}
		</span>
	)
}
